import React, { useMemo } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { CashFlow } from '@api/CashFlows/CashFlow';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';
import { ExpenseListFilters } from '@utils/Expenses';
import ExpensesList from '@components/SalesReport/Expenses/ExpensesList';
import IncomeStatementPanel from '@components/SalesReport/Expenses/IncomeStatementPanel';
import RecurringExpensesPanel from '@components/SalesReport/Expenses/RecurringExpensesPanel';
import ExpenseCategoriesPanel from '@components/SalesReport/Expenses/ExpenseCategoriesPanel';

export type ExpensesSubTab = 'lista' | 'resultados' | 'fijos' | 'categorias';

interface ExpensesTabProps {
    cashFlows: CashFlow[];
    branchOfficeId: string;
    sub: ExpensesSubTab;
    onSubChange: (sub: ExpensesSubTab) => void;
    filters: ExpenseListFilters;
    onFiltersChange: (filters: ExpenseListFilters) => void;
    onChanged: () => void;
}

/**
 * Pestaña Gastos del Informe (plan de Gastos §5.2). Comparte periodo, sucursal y
 * Excel con el resto del Informe. Sub-pestañas según el rol: la lista la ven todos
 * (el colaborador, lo suyo de hoy); el Estado de Resultados, los admins; los gastos
 * fijos, del admin de sucursal para arriba; las categorías, solo el admin general.
 */
const ExpensesTab: React.FunctionComponent<ExpensesTabProps> = ({
    cashFlows,
    branchOfficeId,
    sub,
    onSubChange,
    filters,
    onFiltersChange,
    onChanged
}) => {
    const tabs = useMemo(() => {
        const list: { value: ExpensesSubTab; label: string }[] = [{ value: 'lista', label: 'Lista de gastos' }];
        if (canCurrentUser(Capability.IncomeStatementView)) list.push({ value: 'resultados', label: 'Estado de resultados' });
        if (canCurrentUser(Capability.RecurringExpenseManage)) list.push({ value: 'fijos', label: 'Gastos fijos' });
        if (canCurrentUser(Capability.ExpenseCategoriesManage)) list.push({ value: 'categorias', label: 'Categorías' });
        return list;
    }, []);
    const active = tabs.some((tab) => tab.value === sub) ? sub : 'lista';

    return (
        <Stack height="100%" minHeight={0}>
            {tabs.length > 1 && (
                <Box px={2} borderBottom={1} borderColor="divider" flexShrink={0}>
                    <Tabs value={active} onChange={(_, value) => onSubChange(value)} variant="scrollable" scrollButtons="auto">
                        {tabs.map((tab) => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} sx={{ textTransform: 'none', fontWeight: 600 }} />
                        ))}
                    </Tabs>
                </Box>
            )}
            <Box flex={1} minHeight={0}>
                {active === 'lista' && (
                    <ExpensesList
                        cashFlows={cashFlows}
                        branchOfficeId={branchOfficeId}
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        onChanged={onChanged}
                    />
                )}
                {active === 'resultados' && <IncomeStatementPanel branchOfficeId={branchOfficeId} />}
                {active === 'fijos' && <RecurringExpensesPanel branchOfficeId={branchOfficeId} onChanged={onChanged} />}
                {active === 'categorias' && <ExpenseCategoriesPanel />}
            </Box>
        </Stack>
    );
};

export default ExpensesTab;
